import { Component } from '@theme/component';

export class AnnouncementBar extends Component {
  #current = 0;
  #interval;
  #listeners;
  #motion;
  #paused = false;
  #hover = false;
  #focused = false;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#paused = this.hasAttribute('paused') || this.#motion.matches || this.dataset.designMode === 'true';
    this.addEventListener('mouseenter', () => { this.#hover = true; this.#schedule(); }, options);
    this.addEventListener('mouseleave', () => { this.#hover = false; this.#schedule(); }, options);
    this.addEventListener('focusin', () => { this.#focused = true; this.#schedule(); }, options);
    this.addEventListener('focusout', event => { this.#focused = this.contains(event.relatedTarget); this.#schedule(); }, options);
    document.addEventListener('visibilitychange', () => this.#schedule(), options);
    this.#motion.addEventListener('change', () => { if (this.#motion.matches) this.#paused = true; this.#schedule(); }, options);
    document.addEventListener('shopify:block:select', event => {
      const slide = this.refs.slides?.find(item => item.dataset.shopifyEditorBlock?.includes(event.detail.blockId));
      if (slide) { this.#paused = true; this.current = this.refs.slides.indexOf(slide); this.#schedule(); }
    }, options);
    this.current = 0;
    this.#schedule();
  }

  updatedCallback() {
    super.updatedCallback();
    this.current = this.#current;
    this.#schedule();
  }

  disconnectedCallback() {
    this.suspend();
    this.#listeners?.abort();
    super.disconnectedCallback();
  }

  next() { this.#manual(this.#current + 1); }
  previous() { this.#manual(this.#current - 1); }
  #manual(index) {
    this.#paused = true;
    this.refs.slideshowContainer?.setAttribute('aria-live', 'polite');
    this.current = index;
    this.#schedule();
  }
  togglePlayback() { this.#paused = !this.#paused; this.#schedule(); }
  pause() { this.#paused = true; this.#schedule(); }
  play() { this.#paused = false; this.#schedule(); }
  resume() { this.#schedule(); }
  suspend() { clearInterval(this.#interval); this.#interval = undefined; }
  get autoplayInterval() {
    const value = Number(this.getAttribute('autoplay'));
    return Number.isFinite(value) && value >= 2 ? Math.min(value, 30) * 1000 : 0;
  }
  get autoplay() { return this.autoplayInterval > 0; }
  get paused() { return this.#paused; }
  set paused(value) { this.#paused = Boolean(value); this.#schedule(); }
  #schedule() {
    this.suspend();
    this.toggleAttribute('paused', this.#paused);
    const playback = this.refs.playback;
    if (playback) {
      playback.setAttribute('aria-label', this.#paused ? playback.dataset.playLabel : playback.dataset.pauseLabel);
      playback.querySelector('[data-pause-icon]').hidden = this.#paused;
      playback.querySelector('[data-play-icon]').hidden = !this.#paused;
    }
    if (!this.isConnected || this.#paused || this.#hover || this.#focused || document.hidden || !this.autoplay || (this.refs.slides?.length ?? 0) < 2) return;
    this.refs.slideshowContainer?.setAttribute('aria-live', 'off');
    this.#interval = setInterval(() => { this.current = this.#current + 1; }, this.autoplayInterval);
  }
  get current() { return this.#current; }
  set current(value) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;
    this.#current = ((value % slides.length) + slides.length) % slides.length;
    for (const [index, slide] of slides.entries()) {
      const hidden = index !== this.#current;
      slide.setAttribute('aria-hidden', String(hidden));
      slide.inert = hidden;
    }
  }
}
if (!customElements.get('announcement-bar-component')) customElements.define('announcement-bar-component', AnnouncementBar);
